import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { CheckCircle, Loader2 } from 'lucide-react';
import { getPaymentStatus } from '../utils/api';
import { toast } from 'sonner';

export default function PaymentSuccess({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      toast.error('Invalid payment session');
      navigate('/level-select');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timeout. Please check your account.');
      setChecking(false);
      return;
    }

    try {
      const response = await getPaymentStatus(sessionId);
      const status = response.data;

      if (status.payment_status === 'paid') {
        setPaymentVerified(true);
        setChecking(false);
        toast.success('Payment successful! Premium unlocked!');
        
        // Update user state
        if (user) {
          setUser({ ...user, is_premium: true });
        }
        
        setTimeout(() => {
          navigate('/level-select');
        }, 3000);
      } else if (status.status === 'expired') {
        toast.error('Payment session expired');
        setChecking(false);
        setTimeout(() => navigate('/level-select'), 2000);
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6" 
      data-testid="payment-success-page"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <Card className="max-w-md w-full bg-white/95 backdrop-blur-lg rounded-3xl p-12 text-center relative z-10">
        {checking ? (
          <div data-testid="checking-payment">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#4169E1] animate-spin" />
            <h2 className="text-3xl font-bold text-[#4169E1] mb-4">Verifying Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </div>
        ) : paymentVerified ? (
          <div data-testid="payment-verified">
            <div className="flex justify-center mb-6">
              <div className="bg-green-500 p-6 rounded-full">
                <CheckCircle className="w-16 h-16 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-green-600 mb-6">Thank you for your purchase!</h2>
            <p className="text-xl text-gray-700 font-semibold mb-4">
              Levels 4–6 are now unlocked.
            </p>
            <p className="text-lg text-[#4169E1] font-bold mb-4">
              Keep building your Mega Church!
            </p>
            <p className="text-sm text-gray-500">Redirecting to level select...</p>
          </div>
        ) : (
          <div data-testid="payment-failed">
            <h2 className="text-3xl font-bold text-red-600 mb-4">Payment Verification Failed</h2>
            <p className="text-gray-600">Please contact support if you believe this is an error.</p>
          </div>
        )}

      </Card>
    </div>
  );
}
